/**
 * Structure-import helpers (Beta-1 plan P4-15). Pure.
 *
 *   normalizeProposal(p)        WF7 output → { orgName, industry, divisions[], sites[], departments[], teams[], people[] }
 *                               (older WF7 output without divisions/people still works)
 *   mergeIntoExisting(org, s)   extend mode: append the proposal to an existing org, skipping units whose
 *                               name already exists under the same parent; returns { sites, departments, teams, divisions, added }
 *   withSiteCodes(sites)        every site gets a unique code (P4-19 / P2-06)
 *   rosterFromPeople(people, s) people found in the chart → roster entries (no accounts, no emails) with unit ids
 */
import { deriveSiteCode, isValidSiteCode } from './conditionIds.js';

const squash = (s) => String(s || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
const newId = (prefix) => `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

export function normalizeProposal(p) {
  const src = p || {};
  const divisions = Array.isArray(src.divisions) ? src.divisions.map((d) => ({ id: d.id || newId('div'), name: String(d.name || '').trim() })).filter((d) => d.name) : [];
  const sites = (src.sites || []).map((s) => ({ id: s.id || newId('site'), name: String(s.name || '').trim(), location: s.location || '', region: s.region || '', divisionId: s.divisionId || null, code: s.code || '' }));
  const departments = (src.departments || []).map((d) => ({ id: d.id || newId('dept'), name: String(d.name || '').trim(), siteId: d.siteId }));
  const teams = (src.teams || []).map((t) => ({ id: t.id || newId('team'), name: String(t.name || '').trim(), departmentId: t.departmentId, nwtType: t.nwtType || (/\b(crew|shift)\b/i.test(t.name || '') ? 'crew' : 'team') }));
  const people = Array.isArray(src.people) ? src.people.map((x) => ({ name: String(x.name || '').trim(), title: x.title || '', siteId: x.siteId || null, deptId: x.deptId || x.departmentId || null, nwtId: x.nwtId || x.teamId || null, email: x.email || '' })).filter((x) => x.name) : [];
  return { orgName: src.orgName || '', industry: src.industry || '', divisions, sites, departments, teams, people };
}

export function withSiteCodes(sites) {
  const taken = [];
  return sites.map((s) => {
    const code = isValidSiteCode(s.code) && !taken.includes(s.code) ? s.code : deriveSiteCode(s.name, taken);
    taken.push(code);
    return { ...s, code };
  });
}

/**
 * Append a proposal to an existing organization. Units are matched by name under the same parent;
 * matches reuse the existing id (so their children land in the right place), new ones are added.
 */
export function mergeIntoExisting(org, structure) {
  const sites = [...(org.sites || [])];
  const departments = [...(org.departments || [])];
  const teams = [...(org.teams || [])];
  const divisions = [...(org.divisions || [])];
  const added = { divisions: 0, sites: 0, departments: 0, teams: 0 };
  const divMap = {};
  for (const d of structure.divisions || []) {
    const hit = divisions.find((x) => squash(x.name) === squash(d.name));
    if (hit) divMap[d.id] = hit.id;
    else { divisions.push({ id: d.id, name: d.name }); divMap[d.id] = d.id; added.divisions += 1; }
  }
  const siteMap = {};
  for (const s of structure.sites || []) {
    const hit = sites.find((x) => squash(x.name) === squash(s.name));
    if (hit) siteMap[s.id] = hit.id;
    else { sites.push({ id: s.id, name: s.name, location: s.location || '', ...(s.region ? { region: s.region } : {}), ...(s.divisionId ? { divisionId: divMap[s.divisionId] || s.divisionId } : {}), code: s.code || '' }); siteMap[s.id] = s.id; added.sites += 1; }
  }
  const deptMap = {};
  for (const d of structure.departments || []) {
    const siteId = siteMap[d.siteId] || d.siteId;
    const hit = departments.find((x) => x.siteId === siteId && squash(x.name) === squash(d.name));
    if (hit) deptMap[d.id] = hit.id;
    else { departments.push({ id: d.id, name: d.name, siteId }); deptMap[d.id] = d.id; added.departments += 1; }
  }
  for (const t of structure.teams || []) {
    const departmentId = deptMap[t.departmentId] || t.departmentId;
    const hit = teams.find((x) => x.departmentId === departmentId && squash(x.name) === squash(t.name));
    if (!hit) { teams.push({ id: t.id, name: t.name, departmentId, nwtType: t.nwtType || 'team' }); added.teams += 1; }
  }
  return { sites: withSiteCodes(sites), departments, teams, divisions, added };
}

/** People found in the chart become roster entries (Tim D07.1 "names found?") — no accounts yet. */
export function rosterFromPeople(people, structure) {
  const validSite = new Set((structure.sites || []).map((s) => s.id));
  const validDept = new Set((structure.departments || []).map((d) => d.id));
  const validTeam = new Set((structure.teams || []).map((t) => t.id));
  return (people || []).map((p) => ({
    name: p.name,
    title: p.title || '',
    email: p.email || '',
    siteId: validSite.has(p.siteId) ? p.siteId : null,
    deptId: validDept.has(p.deptId) ? p.deptId : null,
    nwtId: validTeam.has(p.nwtId) ? p.nwtId : null,
    source: 'import',
  }));
}
